import Routes from '@services/Routes';

export const RETURN_TO_PARAM = 'return_to';

// Only the OAuth authorize endpoint on the platform host. This value comes off the URL, so it is
// attacker-supplied: anyone can send a user to `/login?return_to=…`. Restricting it to one exact
// path on one known origin is what keeps the login page from becoming an open redirector.
//
// Mirrors Oauth::ReturnTo in bidops-app, which applies the same allowlist to the SAML RelayState
// leg. Both sides validate, because either can be reached directly.
const PERMITTED_PATH = '/oauth/authorize';

/**
 * Validates a candidate return target: absolute, on the platform origin, and the authorize path.
 */
export const isPermittedReturnTo = (value?: string | null): boolean => {
  if (!value) return false;

  try {
    const target = new URL(value);
    const permittedOrigin = new URL(Routes.BASE_PLATFORM_URL).origin;

    return target.origin === permittedOrigin && target.pathname === PERMITTED_PATH;
  } catch {
    // Not a parseable absolute URL — a bare path, a protocol-relative target, or junk.
    return false;
  }
};

/**
 * Rebuilds a validated target from this app's own constants, carrying over only the query string.
 *
 * Validating a string and then navigating to that same string leaves the origin and path dependent on
 * attacker-supplied input, even though they were checked: the only thing standing between the check
 * and the navigation is that `new URL()` here parses the value exactly as the browser will at
 * navigation time. Building the origin and path from `BASE_PLATFORM_URL` and `PERMITTED_PATH` removes
 * that assumption — the only part that can vary is the query, which is the part that has to survive
 * (client_id, code_challenge, state, resource).
 */
const rebuildFromConstants = (value: string): string => {
  const target = new URL(value);
  const safe = new URL(PERMITTED_PATH, Routes.BASE_PLATFORM_URL);

  safe.search = target.search;

  return safe.toString();
};

/**
 * The validated return target on the current URL, normalised, or null.
 */
export const pendingReturnTo = (): string | null => {
  if (typeof window === 'undefined') return null;

  const value = new URLSearchParams(window.location.search).get(RETURN_TO_PARAM);

  return isPermittedReturnTo(value) ? rebuildFromConstants(value as string) : null;
};

/**
 * Resumes a pending OAuth authorize request in the current window. Returns whether it took over, so
 * callers can fall through to their normal post-login redirect when it did not.
 *
 * Navigation is a full page load, deliberately — not `router.push`. The target is on another origin,
 * and more importantly this has to be a *fresh* navigation initiated by this app: continuing the
 * redirect chain that began at claude.ai keeps the request cross-site, which drops the
 * SameSite=Strict `jwt` cookie on every hop and turns the flow into an endless
 * authorize → login → authorize bounce. Measured by the SameSite spike; see
 * https://arkestrodev.atlassian.net/wiki/spaces/EP/pages/1212579844
 *
 * For the password login this is the right window. The SSO callback needs the variant below.
 */
export const resumeAuthorizeIfPending = (): boolean => {
  const returnTo = pendingReturnTo();

  if (!returnTo || typeof window === 'undefined') return false;

  window.location.assign(returnTo);

  return true;
};

/**
 * Resumes a pending authorize request in a *named* window rather than this one, and reports whether
 * it did.
 *
 * The SSO callback runs inside the 800x800 popup that SSOLoginForm opened, so resuming in the current
 * window would render the consent screen in that popup and leave the user's real tab behind. Handing
 * the target to the primary window by name is the same mechanism the existing SSO callback already
 * uses to return the user to the app, and the resulting navigation is still one this app initiates —
 * which is what keeps the SameSite=Strict `jwt` cookie attached.
 */
export const resumeAuthorizeInWindow = (windowName: string): boolean => {
  const returnTo = pendingReturnTo();

  if (!returnTo || typeof window === 'undefined') return false;

  window.open(returnTo, windowName, '');

  return true;
};
